import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { cryptoPairLabel, isCryptoSymbol } from '../composables/fetchCrypto';
import { isCryptoPortfolio, getCryptoPriceSymbol } from '../utils/portfolioType';
import { formatCryptoQty, formatCryptoUsd, shortenDecimalText, num } from '../utils/cryptoDecimal';
import { checkAllSignalUpdates, getAllPortfoliosTwo, importSelectedSignals, myRequest } from '../composables/fetchAPI';
import { EMPTY_SIGNAL_SYNCS, SignalAssetType, getSignalSync, getSignalSyncs, setSignalSync, signalAssetTypeOf } from '../utils/signalSync';
import TradingView from '../components/TradingView';
import TableCalculate from '../components/TableCalculate';
import AverageCalculate from '../components/AverageCalculate';
import ArticleByAI from '../components/ArticleByAI';
import Favorite from '../components/Favorite';
import PortfolioSummaryList from '../components/PortfolioSummaryList';
import ManagePortfolioV2 from '../components/ManagePortfolioV2';
import LossCalculator from '../components/LossCalculator';
import { useExchangeRates } from "../context/ExchangeRateContext";
import { fetchPolygonSnapshotPriceses } from '../composables/fetchPolygon';
import SignalUpdateList from '../components/SignalUpdateList';
import { toast } from 'react-hot-toast';
import ImportConflictModal from '../components/ImportConflictModal';
import RequestBox from '../components/RequestBox';
import { usePortfolio } from '../context/PortfolioContext'; // อัปเดต Path ให้ตรงกับของคุณ
import { useMarketPrices } from '../context/MarketPriceContext'; // ดึง Context ราคามาใช้

// 1. ฟังก์ชัน Helper สำหรับอ่านค่าจาก LocalStorage (กัน Error เรื่อง JSON)
const getStoredToggle = (key: string, defaultValue: boolean) => {
  const saved = localStorage.getItem(key);
  return saved !== null ? JSON.parse(saved) : defaultValue;
};

interface HomePageProps {
  selectedSymbol: string;
  setSelectedSymbol: (symbol: string) => void;
  userInfo: any; // เพิ่ม userInfo ใน props
  setAllHistoryDataForArticle: (data: any[]) => void; // ฟังก์ชันสำหรับส่งข้อมูลประวัติทั้งหมดไปยัง App.tsx
}

const HomePage: React.FC<HomePageProps> = ({ selectedSymbol, setSelectedSymbol, userInfo, setAllHistoryDataForArticle }) => {
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<string>('');
  const [freestyleSR, setFreestyleSR] = useState<{ [symbol: string]: { tag: string; price: number | null }[] }>({});
  const [autoSR, setAutoSR] = useState<{ [symbol: string]: { tag: string; price: number | null }[] }>({});
  const [priceLineType, setPriceLineType] = useState<'freestyle' | 'support-resistance'>('freestyle');
  const [avgBuyPrice, setAvgBuyPrice] = useState<number | null>(null);
  const [aiPayload, setAiPayload] = useState<any>(null);
  const [showTableCalculate, setShowTableCalculate] = useState<boolean>(() =>
    getStoredToggle('ui_showTableCalculate', true)
  );
  const [showAverageCalculate, setShowAverageCalculate] = useState<boolean>(() =>
    getStoredToggle('ui_showAverageCalculate', true)
  );
  const [showManagePortfolio, setShowManagePortfolio] = useState<boolean>(() =>
    getStoredToggle('ui_showManagePortfolio', true)
  );
  const [showLossCalculator, setShowLossCalculator] = useState<boolean>(() =>
    getStoredToggle('ui_showLossCalculator', true)
  );
  // const [portfolios, setPortfolios] = useState([]);
  const { userPortfolios: portfolios, refreshUserPortfolios,displayMode } = usePortfolio();
  const [modeLoss, setModeLoss] = useState<string>('default');
  const [customPrice, setCustomPrice] = useState<number | ''>('');
  const [selectedSubPortId, setSelectedSubPortId] = useState<string>('');
  const [updateTrigger, setUpdateTrigger] = useState(false);
  const { exchangeRates } = useExchangeRates();
  const { currentPrices, fetchPricesForSymbols } = useMarketPrices();
  const leftColScrollRef = useRef<HTMLDivElement>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);

  // --- Draggable grid column widths (desktop only) ---
  const DEFAULT_COL_WIDTHS = { left: 14, middle: 53, right: 35 };
  const MIN_COL_PERCENT = 10;
  const SNAP_THRESHOLD = 1.5; // % ระยะที่ลากเข้าใกล้ค่า Default แล้วจะ Snap ล็อกกลับไปที่ค่านั้น
  const [colWidths, setColWidths] = useState<{ left: number; middle: number; right: number }>(() => {
    const saved = localStorage.getItem('ui_homeColWidths');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed.left === 'number' && typeof parsed.middle === 'number' && typeof parsed.right === 'number') {
          return parsed;
        }
      } catch { /* ignore malformed value */ }
    }
    return DEFAULT_COL_WIDTHS;
  });
  const dragInfoRef = useRef<{ boundary: 0 | 1; startX: number; startLeft: number; startMiddle: number; startRight: number } | null>(null);
  const [isDraggingCol, setIsDraggingCol] = useState(false);

  useEffect(() => {
    localStorage.setItem('ui_homeColWidths', JSON.stringify(colWidths));
  }, [colWidths]);

  const lastTapRef = useRef(0);

  // ใช้ Pointer Events แทน Mouse Events เพื่อให้ลากได้ทั้งเมาส์ นิ้ว (iPad) และ Apple Pencil
  const handleColDragStart = (boundary: 0 | 1) => (e: React.PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();

    // จอสัมผัสไม่มี dblclick ที่เชื่อถือได้ เลยจับ double-tap เองเพื่อรีเซ็ตความกว้าง
    if (e.pointerType !== 'mouse') {
      const now = Date.now();
      if (now - lastTapRef.current < 300) {
        lastTapRef.current = 0;
        setColWidths(DEFAULT_COL_WIDTHS);
        return;
      }
      lastTapRef.current = now;
    }

    dragInfoRef.current = {
      boundary,
      startX: e.clientX,
      startLeft: colWidths.left,
      startMiddle: colWidths.middle,
      startRight: colWidths.right,
    };
    setIsDraggingCol(true);
  };

  useEffect(() => {
    if (!isDraggingCol) return;

    const handlePointerMove = (e: PointerEvent) => {
      const info = dragInfoRef.current;
      if (!info || !gridContainerRef.current) return;
      const containerWidth = gridContainerRef.current.offsetWidth;
      if (!containerWidth) return;
      const deltaPercent = ((e.clientX - info.startX) / containerWidth) * 100;

      if (info.boundary === 0) {
        const pairTotal = info.startLeft + info.startMiddle;
        let newLeft = Math.min(Math.max(info.startLeft + deltaPercent, MIN_COL_PERCENT), pairTotal - MIN_COL_PERCENT);
        if (Math.abs(newLeft - DEFAULT_COL_WIDTHS.left) < SNAP_THRESHOLD) newLeft = DEFAULT_COL_WIDTHS.left;
        setColWidths({ left: newLeft, middle: pairTotal - newLeft, right: info.startRight });
      } else {
        const pairTotal = info.startMiddle + info.startRight;
        let newMiddle = Math.min(Math.max(info.startMiddle + deltaPercent, MIN_COL_PERCENT), pairTotal - MIN_COL_PERCENT);
        if (Math.abs(newMiddle - DEFAULT_COL_WIDTHS.middle) < SNAP_THRESHOLD) newMiddle = DEFAULT_COL_WIDTHS.middle;
        setColWidths({ left: info.startLeft, middle: newMiddle, right: pairTotal - newMiddle });
      }
    };

    const handlePointerUp = () => {
      setIsDraggingCol(false);
      dragInfoRef.current = null;
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [isDraggingCol]);

  useEffect(() => {
    if (isDraggingCol) {
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    } else {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDraggingCol]);
  const [activeDots, setActiveDots] = useState([0, 0, 0]);
  const [signalData, setSignalData] = useState<any[]>([]);
  const [isSignalLoading, setIsSignalLoading] = useState(false);
  const [signalError, setSignalError] = useState<string | null>(null);
  const [symbol, setSymbol] = useState(localStorage.getItem('selectedSymbol'));
  const [manageSummary, setManageSummary] = useState<React.ReactNode>(null);
  const [allHistoryData, setAllHistoryData] = useState<any[]>([]);
  const [fullSignalList, setFullSignalList] = useState<any[]>([]);
  const [isPriceLoading, setIsPriceLoading] = useState(false); // ✅ เพิ่ม State ตัวนี้
  const [userRequests, setUserRequests] = useState<any[]>([]); // เก็บประวัติการ Request ของ User
  const [importModal, setImportModal] = useState<{
    isOpen: boolean;
    symbol: string;
    signal: any;
  }>({ isOpen: false, symbol: '', signal: null });
  
  const fetchUserRequests = useCallback(async () => {
    try {
      // สมมติว่าพี่มีฟังก์ชัน getMyRequests ใน fetchAPI.ts นะครับ
      // ถ้ายังไม่มี ให้ใช้ axios.get('/api/myRequest') ตามปกติ
      const response = await myRequest();
      // console.log("User Requests Response:", response.items);
      if (response && response.items) {
        setUserRequests(response.items);
      }
    } catch (error) {
      console.error("Error fetching user requests:", error);
    }
  }, []);

  // สั่งรันตอนโหลดหน้าจอ
  useEffect(() => {
    fetchUserRequests();
  }, [fetchUserRequests]);

  // ✅ ย้าย handleUserImport มาไว้ที่นี่
  const handleUserImportFromSignal = async (
    targetSymbol: string | string[],
    isBulkImport = false,
    forceReplace = false
  ) => {
    // --- 1. เตรียมข้อมูล Symbols ที่ต้องการจัดการ ---
    const tickersToProcess = Array.isArray(targetSymbol) ? targetSymbol : [targetSymbol];
    // console.log("Importing for symbols:", fullSignalList);
    // --- 2. ค้นหาข้อมูล Signal จากลิสต์ที่มีอยู่ (รองรับทั้ง ticker และ symbol) ---
    const signalsToProcess = fullSignalList.filter(s =>
      tickersToProcess.includes(s.ticker) || tickersToProcess.includes(s.symbol)
    );

    if (signalsToProcess.length === 0) {
      console.warn("No signal data found for:", targetSymbol);
      return;
    }

    // --- 3. กรณี "Import รายตัว" (กดจากหน้าพอร์ต หรือ TradingView) ---
    if (!Array.isArray(targetSymbol)) {
      const symbol = targetSymbol;
      const currentSignal = signalsToProcess[0];

      // ดึงข้อมูลเดิมมาเช็ค
      const existingData = localStorage.getItem(`chartLines_${symbol}`);
      const hasData = existingData &&
        existingData !== '{"lines":[],"supportTags":[],"resistanceTags":[]}' &&
        existingData !== '[]';

      // โลจิกตัดสินใจ:
      // ถ้า forceReplace = true (กดจาก TradingView) -> ข้ามไปทับเลย ไม่ต้องถาม
      // ถ้าไม่มีข้อมูลเดิม หรือเป็น Bulk -> ทับเลย
      if (forceReplace || isBulkImport || !hasData) {
        // ส่ง false เข้าไปใน parameter สุดท้ายของ executeImport เพื่อไม่ให้ทับเวลา Sync
        return await executeImport(symbol, currentSignal, 'REPLACE', isBulkImport, !forceReplace);
      } else {
        // ถ้ามีข้อมูลอยู่แล้ว และไม่ได้สั่ง Force -> เปิด Popup ถาม (Merge/Replace)
        setImportModal({ isOpen: true, symbol, signal: currentSignal });
      }
    }

    // --- 4. กรณี "Import ทั้งหมด" (Bulk) ---
    else {
      return await executeImport(tickersToProcess, signalsToProcess, 'REPLACE', true, true);
    }
  };

  // ฟังก์ชันหลักที่ใช้ยิง API และบันทึกข้อมูล (แยกออกมาเพื่อให้เรียกใช้ได้จากหลายที่)
  const executeImport = async (
    targetSymbols: string | string[],
    signals: any | any[],
    mode: 'REPLACE' | 'MERGE',
    isBulk: boolean,
    shouldUpdateSync = true // เพิ่มตัวแปรเช็คว่าจะอัปเดตเวลา Sync หรือไม่
  ) => {
    try {
      const tickerList = Array.isArray(targetSymbols) ? targetSymbols : [targetSymbols];
      const signalList = Array.isArray(signals) ? signals : [signals];

      // หลังบ้านแยกโหมด STOCK / CRYPTO → ยิง import แยกตามโหมด พร้อม syncKey ของโหมดนั้น (Import All อาจมีทั้งสองแบบ)
      const groups = new Map<SignalAssetType, { tickers: string[]; signalIds: string[] }>();
      const groupOf = (type: SignalAssetType) => {
        if (!groups.has(type)) groups.set(type, { tickers: [], signalIds: [] });
        return groups.get(type)!;
      };
      tickerList.forEach(t => groupOf(signalAssetTypeOf(t)).tickers.push(t));
      signalList.forEach(s => groupOf(signalAssetTypeOf(s.ticker || s.symbol)).signalIds.push(s.id));

      let result = true;
      for (const [assetType, group] of Array.from(groups.entries())) {
        const groupResult = await importSelectedSignals({
          ...group,
          assetType,
          sync: shouldUpdateSync ? getSignalSync(assetType) : ""
        });
        if (!groupResult) {
          result = false;
          continue;
        }
        // เงื่อนไขใหม่: เช็คว่าควรบันทึก Sync Time หรือไม่ (เก็บแยก key ตามโหมด)
        if (shouldUpdateSync) {
          const newSyncKey = groupResult.syncKey || groupResult.latest_sync;
          if (newSyncKey) setSignalSync(assetType, newSyncKey.toString());
        }
      }

      if (result) {
        signalList.forEach((signal, index) => {
          const symbol = tickerList[index];
          // ✅ 1. เพิ่ม Logic แปลง Format ข้อมูล (รองรับทั้งแบบ lines และ S_line/R_line)
          let rawLines = [];
          if (signal.details.lines) {
            rawLines = signal.details.lines;
          } else if (signal.details.S_line || signal.details.R_line) {
            // แปลง S_line Object เป็น Array
            const sLines = Object.entries(signal.details.S_line || {}).map(([tag, price]) => ({
              tag,
              price: Number(price),
              type: 'support'
            }));
            // แปลง R_line Object เป็น Array
            const rLines = Object.entries(signal.details.R_line || {}).map(([tag, price]) => ({
              tag,
              price: Number(price),
              type: 'resistance'
            }));
            rawLines = [...sLines, ...rLines];
          }

          let finalLines = rawLines;

          // ✅ 2. ปรับการ Merge ให้ใช้ rawLines ที่แปลงแล้ว
          if (mode === 'MERGE') {
            const localRaw = localStorage.getItem(`chartLines_${symbol}`);
            const localParsed = localRaw ? JSON.parse(localRaw) : { lines: [] };
            const combinedRawLines = [...localParsed.lines, ...rawLines]; // ใช้ rawLines

            const uniqueLines = combinedRawLines?.filter((line, idx, self) =>
              idx === self.findIndex((t) => t.price === line.price)
            );

            const supports = uniqueLines?.filter(l => l.type === 'support').sort((a, b) => b.price - a.price);
            const resistances = uniqueLines?.filter(l => l.type === 'resistance').sort((a, b) => a.price - b.price);
            finalLines = [
              ...supports.map((l, idx) => ({ ...l, tag: `S${idx + 1}` })),
              ...resistances.map((l, idx) => ({ ...l, tag: `R${idx + 1}` }))
            ];
          }

          const dataToSave = {
            lines: finalLines,
            supportTags: finalLines?.filter((l: any) => l.type === 'support').map((l: any) => l.tag),
            resistanceTags: finalLines?.filter((l: any) => l.type === 'resistance').map((l: any) => l.tag),
          };

          localStorage.setItem(`chartLines_${symbol}`, JSON.stringify(dataToSave));
        });
        await handleFetchSignals();
        toast.success(`นำเข้าสำเร็จ ${tickerList.length} รายการ!`);

        // 4. จัดการเรื่องหน้าจอ
        if (!isBulk && !Array.isArray(targetSymbols)) {
          localStorage.setItem('selectedSymbol', targetSymbols);
          setSymbol(targetSymbols);
        }

        // รีโหลดหน้าจอเพื่อให้ State ทุกอย่างเคลียร์
        setTimeout(() => window.location.reload(), 1000);
      }
    } catch (err: any) {
      toast.error(`เกิดข้อผิดพลาด: ${err.message}`);
    }
  };

  const showNewTagOnImportBtn = useMemo(() => {
    if (!selectedSymbol || !signalData) return false;

    // ตรวจสอบว่าชื่อหุ้นปัจจุบัน มีรายชื่อค้างอยู่ในกล่อง signalData หรือไม่
    return signalData.some(
      (item: any) => (item.ticker === selectedSymbol || item.symbol === selectedSymbol)
    );
  }, [selectedSymbol, signalData]);

  const manageData = useMemo(() => {
    if (portfolios.length === 0 || !selectedSymbol) return null;

    const stocks: any[] = [];
    portfolios.forEach((portfolio: any) => {
      portfolio.subPorts?.forEach((subPort: any) => {
        // ✅ 1. รองรับทั้ง V2 (holdings) และ V1 (stocks)
        const items = subPort.holdings || subPort.stocks || [];

        // พอร์ตคริปโต: assetSymbol เป็น BTC/USD แต่กราฟเลือกด้วย X:BTCUSD → เทียบด้วย providerSymbol
        const isCrypto = isCryptoPortfolio(portfolio);

        items.forEach((stock: any) => {
          // ✅ 2. รองรับชื่อฟิลด์หุ้นทุกแบบ
          const sym = isCrypto ? getCryptoPriceSymbol(stock) : (stock.assetSymbol || stock.stockSymbol || stock.symbol);
          const isClosed = stock.holdingStatus === 'CLOSED' || stock.status === 'CLOSED';

          if (sym === selectedSymbol && !isClosed) {
            stocks.push({
              ...stock,
              // ✅ 3. Map ชื่อกลับให้ตรงกับที่ UI เรียกใช้ จะได้ไม่ต้องแก้โค้ด UI
              stockSymbol: sym,
              isCrypto,
              // คริปโตส่งเป็น string (toFixed บน string จะพัง) → แปลงเป็นตัวเลขไว้คำนวณ และเก็บค่าเดิมไว้แสดงจำนวนเหรียญ
              quantityRaw: stock.quantity,
              quantity: num(stock.quantity),
              avgBuyPrice: num(stock.avgEntryPrice || stock.avgBuyPrice || 0),
              portId: portfolio.portfolioId || portfolio.portId || portfolio.id,
              portName: portfolio.name,
              subPortId: subPort.subPortId,
              subPortName: subPort.name,
            });
          }
        });
      });
    });

    const currentPrice = currentPrices[selectedSymbol] || 0;
    // หาพอร์ตที่เลือกอยู่จาก ID
    const selected = stocks.find(s => s.subPortId === selectedSubPortId) || stocks[0];

    return selected ? { selected, allMatches: stocks, currentPrice } : null;
  }, [portfolios, selectedSymbol, currentPrices, selectedSubPortId]);

  useEffect(() => {
    if (isMobile && leftColScrollRef.current) {
      // สั่งให้เลื่อนไปที่ตำแหน่ง 0 ทันทีที่โหลด
      leftColScrollRef.current.scrollLeft = 0;
    }
  }, [isMobile]);

  // 3. ใช้ useEffect เพื่อดักจับการเปลี่ยนแปลงแล้ว Save ลง Storage
  useEffect(() => {
    localStorage.setItem('ui_showTableCalculate', JSON.stringify(showTableCalculate));
  }, [showTableCalculate]);

  useEffect(() => {
    localStorage.setItem('ui_showAverageCalculate', JSON.stringify(showAverageCalculate));
  }, [showAverageCalculate]);

  useEffect(() => {
    localStorage.setItem('ui_showManagePortfolio', JSON.stringify(showManagePortfolio));
  }, [showManagePortfolio]);

  useEffect(() => {
    localStorage.setItem('ui_showLossCalculator', JSON.stringify(showLossCalculator));
  }, [showLossCalculator]);


  useEffect(() => {
    const portSymbols = portfolios.flatMap((p: any) =>
      (p.subPorts || []).flatMap((sp: any) =>
        (sp.holdings || []).map((h: any) => h.symbol || h.assetSymbol)
      )
    );

    if (selectedSymbol) portSymbols.push(selectedSymbol);
    const uniqueSymbols = Array.from(new Set(portSymbols)).filter(Boolean);

    if (uniqueSymbols.length > 0) {
      // ✅ เรียกใช้จาก Context แทน มันจะฉลาดพอที่จะดึงเฉพาะหุ้นที่ "ยังไม่มีราคา" เท่านั้น
      fetchPricesForSymbols(uniqueSymbols);
    }
  }, [portfolios, selectedSymbol]);

  // useEffect(() => {
  //   let isMounted = true; // ✅ กัน Update ตอน Unmount

  //   async function fetchAllPrices() {
  //     // 1. ดึง Symbols ทั้งหมด (ทั้งในพอร์ตและตัวที่เลือก)
  //     const portSymbols = portfolios.flatMap((p: any) =>
  //       (p.subPorts || []).flatMap((sp: any) =>
  //         // ✅ เปลี่ยนเป็น sp.holdings และหาค่า .symbol แทน
  //         (sp.holdings || []).map((h: any) => h.symbol || h.assetSymbol)
  //       )
  //     );

  //     if (selectedSymbol) portSymbols.push(selectedSymbol);
  //     const uniqueSymbols = Array.from(new Set(portSymbols)).filter((s): s is string => Boolean(s));

  //     if (uniqueSymbols.length === 0) return;

  //     try {
  //       // ✅ 2. เริ่ม Fetch
  //       const newPrices = await fetchPolygonSnapshotPriceses(uniqueSymbols);

  //       if (isMounted && newPrices) {
  //         // 🔥 จุดตาย: ต้องใช้ Functional Update เพื่อ "Merge" ข้อมูลเดิมเข้ากับข้อมูลใหม่
  //         setCurrentPrices(prevPrices => ({
  //           ...prevPrices, // ข้อมูลราคาเดิมที่มีอยู่
  //           ...newPrices   // อัปเดตราคาใหม่เข้าไปทับตัวที่เปลี่ยน
  //         }));
  //       }
  //     } catch (error) {
  //       console.error('Error fetching prices:', error);
  //     }
  //   }

  //   fetchAllPrices();

  //   return () => { isMounted = false; }; // ✅ Cleanup function
  // }, [portfolios, selectedSymbol]); // dependency เหมือนเดิม

  useEffect(() => {
    const checkSize = () => setIsMobile(window.innerWidth < 768);
    checkSize();
    window.addEventListener('resize', checkSize);
    return () => window.removeEventListener('resize', checkSize);
  }, []);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>, colIdx: number) => {
    const idx = Math.round(e.currentTarget.scrollLeft / e.currentTarget.offsetWidth);
    setActiveDots(prev => {
      const next = [...prev];
      next[colIdx] = idx;
      return next;
    });
  };

  const handleSelectPortfolio = (portfolioId: string, subPortId: string) => {
    setSelectedPortfolioId(portfolioId);
    setSelectedSubPortId(subPortId);

  };

  // const fetchPortfolios = async () => {
  //   try {
  //     const response = await getAllPortfoliosTwo();
  //     setPortfolios(response.portfolios);
  //   } catch (error) { console.error(error); }
  // };

  // useEffect(() => { fetchPortfolios(); }, []);

  useEffect(() => {
    setAiPayload(null);
    setFreestyleSR((prev) => ({
      ...prev,
      [selectedSymbol]: prev[selectedSymbol] || [],
    }));
    setAutoSR((prev) => ({
      ...prev,
      [selectedSymbol]: prev[selectedSymbol] || [],
    }));
  }, [selectedSymbol]);

  const handleIndicatorsReady = (data: any) => {
    if (data.symbol !== selectedSymbol) return;
    setAiPayload({ symbol: data.symbol, ohlcv: data.ohlcv, indicators: data.indicators, locale: "th", objective: "trading", riskProfile: "medium" });
  };

  const handleSupportResistanceUpdate = (incoming: any) => {
    // ใช้ Functional Update เพื่อให้ได้ค่า State ล่าสุดเสมอ
    setFreestyleSR((prev) => {
      const currentSymbol = selectedSymbol; // มั่นใจว่าใช้ symbol ปัจจุบัน
      const currentList = prev[currentSymbol] || [];
      let newList: any[] = [];

      // กรณีส่งมาเป็นก้อน Array (จากจังหวะ MouseUp หรือ Clear All)
      if (Array.isArray(incoming)) {
        newList = [...incoming];
      }
      // กรณีส่งมาเป็น Object เส้นเดียว (จากจังหวะลากเส้นใหม่)
      else if (incoming && typeof incoming === 'object') {
        if (incoming.price === null) {
          newList = currentList.filter((l: any) => l.tag !== incoming.tag);
        } else {
          const filtered = currentList.filter((l: any) => l.tag !== incoming.tag);
          newList = [...filtered, incoming];
        }
      }

      // --- ตรวจสอบเพื่อหยุด Infinite Loop ---
      // 1. ถ้าความยาวไม่เท่ากัน อัปเดตทันที (เช่น ลบเส้น หรือ เพิ่มเส้น)
      if (currentList.length !== newList.length) {
        return { ...prev, [currentSymbol]: newList };
      }

      // 2. ถ้าความยาวเท่ากัน ให้เช็คเนื้อหาข้างใน
      if (JSON.stringify(currentList) !== JSON.stringify(newList)) {
        return { ...prev, [currentSymbol]: newList };
      }

      // 3. ถ้าเหมือนเดิมเป๊ะ ไม่ต้องทำอะไร (หยุด Loop ตรงนี้)
      return prev;
    });
  };

  const Dot = ({ count, activeIdx }: { count: number, activeIdx: number }) => (
    <div className="flex justify-center gap-1.5 mt-2 md:hidden">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className={`h-1.5 rounded-full transition-all ${i === activeIdx ? 'w-4 bg-yellow-500' : 'w-1.5 bg-gray-600'}`} />
      ))}
    </div>
  );

  const scrollBox = "flex md:block overflow-x-auto snap-x snap-mandatory no-scrollbar gap-4";
  const scrollItem = "min-w-full snap-center md:min-w-0";
  
  const defaultPortfolioInfo = useMemo(() => {
  if (!portfolios || !selectedSymbol) return { portId: '', subPortId: '' };

  for (const p of portfolios) {
    // ✅ เพิ่มการเช็คฟิลด์ให้ครอบคลุมทั้ง 2 โหมด
    const subPorts = p.subPorts || p.subPortfolios || [];
    const sub = subPorts.find((sp: any) => {
      const holdings = sp.holdings || sp.stocks || [];
      return holdings.some((s: any) =>
        (s.symbol || s.assetSymbol || s.stockSymbol) === selectedSymbol &&
        s.holdingStatus !== 'CLOSED' && s.status !== 'CLOSED'
      );
    });

    if (sub) {
      return {
        portId: p.portfolioId || p.portId || p.id,
        subPortId: sub.subPortId || sub.subPortfolioId
      };
    }
  }
  return { portId: '', subPortId: '' };
}, [portfolios, selectedSymbol, displayMode]); // displayMode สำคัญมาก

  useEffect(() => {
    if (!selectedSymbol || portfolios.length === 0) return;

    const isActiveMatch = (s: any) =>
      (s.symbol || s.assetSymbol || s.stockSymbol) === selectedSymbol &&
      s.holdingStatus !== 'CLOSED' && s.status !== 'CLOSED';

    const matchingPort: any = portfolios.find((p: any) =>
      p.subPorts?.some((sp: any) => {
        // รองรับทั้ง V1 และ V2
        const items = sp.holdings || sp.stocks || [];
        return items.some(isActiveMatch);
      })
    );

    if (matchingPort) {
      const subPort: any = matchingPort.subPorts.find((sp: any) => {
        const items = sp.holdings || sp.stocks || [];
        return items.some(isActiveMatch);
      });

      // อัปเดต ID ทันที
      if (subPort) {
        setSelectedPortfolioId(matchingPort.portfolioId || matchingPort.portId || matchingPort.id);
        setSelectedSubPortId(subPort.subPortId || subPort.subPortfolioId);
      }
    }
  }, [selectedSymbol, portfolios]);

  // 1. กำหนดค่าคงที่สำหรับ 15 นาที (15 * 60 * 1000 มิลลิวินาที)
  // 1. กำหนดค่าคงที่ (15 นาที)
  const REFRESH_INTERVAL = 15 * 60 * 1000;

  // 2. ปรับ handleFetchSignals ให้ฉลาดขึ้น (ใช้ latest_sync และ Merge ข้อมูล)
  const handleFetchSignals = useCallback(async (isFull = false) => {
    setIsSignalLoading(true);
    setSignalError(null);

    try {
      // หุ้นกับคริปโตใช้ syncKey แยกกัน
      const data = await checkAllSignalUpdates(isFull ? EMPTY_SIGNAL_SYNCS : getSignalSyncs());

      if (data && data.signals) {
        // ✅ บังคับ Sort ข้อมูลใหม่ไปเก่าเสมอ
        const sortedNewData = [...data.signals].sort((a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );

        if (isFull) {
          // 📥 กรณี Fetch ทั้งหมด: เก็บเข้า State แยก (allHistoryData)
          // ไม่ต้องเอาไปรวมกับของเก่า เพื่อให้ได้ลิสต์ที่สะอาดตามที่ Admin ส่งมา
          setAllHistoryData(sortedNewData);
          // อัปเดตข้อมูลไปยัง App.tsx ผ่าน Props
          setAllHistoryDataForArticle(sortedNewData);
          // console.log("Fetched full signal list:", sortedNewData);
        } else {
          // 🔄 กรณี Fetch ปกติ (หน้าหลัก/แท็บอัปเดต): ทำการ Merge เข้า signalData เดิม
          setSignalData((prev) => {
            const signalMap = new Map(prev.map(s => [s.ticker, s]));
            data.signals.forEach((newSig: any) => signalMap.set(newSig.ticker, newSig));

            return Array.from(signalMap.values()).sort((a: any, b: any) =>
              new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
            );
          });
        }
      }
    } catch (err: any) {
      setSignalError(err.message);
    } finally {
      setIsSignalLoading(false);
    }
  }, []);

  // 2. Fetch ข้อมูลทันทีที่โหลดหน้าจอ (ปรับปรุงใหม่)
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // ✅ 1. ดึงประวัติทั้งหมดมาเก็บไว้ใน allHistoryData ทันที
        // เพื่อให้ useMemo(canRequestAnalysis) มีข้อมูลไว้เช็คปุ่ม Request ตั้งแต่แรก
        await handleFetchSignals(true);

        // ✅ 2. ดึงข้อมูล Signal ล่าสุด (ถ้ามีฟังก์ชันแยก)
        const response = await checkAllSignalUpdates(EMPTY_SIGNAL_SYNCS);
        if (response && response.signals) {
          setFullSignalList(response.signals);
        }
      } catch (error) {
        console.error("Error fetching initial SR list:", error);
      }
    };

    loadInitialData();
  }, [handleFetchSignals]); // รันครั้งเดียวตอน Mount

  // 3. useEffect สำหรับรัน Interval
  useEffect(() => {
    // โหลดครั้งแรกทันที
    handleFetchSignals();

    const intervalId = setInterval(() => {
      handleFetchSignals();
    }, REFRESH_INTERVAL);

    return () => clearInterval(intervalId);
  }, [handleFetchSignals]); // รันใหม่เฉพาะเมื่อ handleFetchSignals เปลี่ยน (ซึ่งจะไม่เปลี่ยนเพราะ [] ด้านบน)

  // ใน HomePage.tsx
  useEffect(() => {
    const checkAutoImport = async () => {
      const commandRaw = localStorage.getItem('auto_import_signal');
      if (!commandRaw) return;

      try {
        const command = JSON.parse(commandRaw);

        // 1. เช็คความสดใหม่ของคำสั่ง (ไม่เกิน 15 วินาที)
        if (Date.now() - command.timestamp < 15000) {
          const ticker = command.ticker;

          // 2. ค้นหาข้อมูลจาก fullSignalList (ถังข้อมูลทั้งหมดที่เราดึงมาตอนโหลดหน้า)
          // ต้องมั่นใจว่า fullSignalList มีข้อมูลแล้ว
          if (fullSignalList.length > 0) {
            const targetSignal = fullSignalList.find(s => s.ticker === ticker);

            if (targetSignal) {
              // console.log(`🚀 Start Auto-importing for ${ticker}`);

              // ลบคำสั่งออกทันทีเพื่อป้องกัน Loop
              localStorage.removeItem('auto_import_signal');

              // 3. เรียกใช้ executeImport โดยตรง (ข้ามหน้าต่าง Confirm ไปเลยเพราะกดมาจากหน้าข่าวแล้ว)
              // mode: 'REPLACE', isBulk: false, shouldUpdateSync: true
              await executeImport(ticker, targetSignal, 'REPLACE', false, true);
            }
          }
        } else {
          // ถ้าคำสั่งเก่าเกินไปให้ลบทิ้ง
          localStorage.removeItem('auto_import_signal');
        }
      } catch (e) {
        console.error("Auto import error:", e);
        localStorage.removeItem('auto_import_signal');
      }
    };

    // รันเมื่อ fullSignalList พร้อม (ซึ่งจะมาจากการโหลด initial data ครั้งแรก)
    if (fullSignalList.length > 0) {
      checkAutoImport();
    }
  }, [fullSignalList, executeImport]); // ใช้ executeImport เป็น dependency

  const requestQuota = useMemo(() => {
    const today = new Date().toISOString().split('T')[0]; // ได้ "2026-05-14"

    // กรองเฉพาะรายการที่สร้างวันนี้
    const todayRequests = userRequests.filter(r => r.created_at.startsWith(today));

    return {
      used: todayRequests.length,
      total: 3,
      isFull: todayRequests.length >= 3
    };
  }, [userRequests]);

  // 1. ฟังก์ชันตัดเกณฑ์ตัดสินใจ (Pure Function เพื่อความแม่นยำ)
  const verifySRQuotaCount = (adminLines: any[], currentPrice: number): boolean => {
    let dynamicSupportCount = 0;
    let dynamicResistanceCount = 0;

    adminLines.forEach((s: any) => {
      if (Number(s.price) <= currentPrice) {
        dynamicSupportCount++;
      } else {
        dynamicResistanceCount++;
      }
    });

    // คืนค่า true หากพบว่าแนวรับ หรือ แนวต้าน ฝั่งใดฝั่งหนึ่งเหลือ <= 2 เส้น
    return dynamicSupportCount <= 2 || dynamicResistanceCount <= 2;
  };

  const handleImportAndVerifyRequest = async (targetSymbol: string): Promise<{ success: boolean; reason?: 'STALE' | 'ERROR' }> => {
    try {
      // A. ดึงข้อมูลประวัติและ Signal ล่าสุดแบบสดๆ จากเซิร์ฟเวอร์วินาทีนั้นเลย
      const [historyResponse, requestResponse] = await Promise.all([
        checkAllSignalUpdates(EMPTY_SIGNAL_SYNCS), // ดึงข้อมูลอัปเดตล่าสุดทั้งหมด (หุ้น + คริปโต)
        myRequest()             // ดึงประวัติ Quota ประจำวัน
      ]);

      // อัปเดต Quota และรายการหลักหน้าบ้านให้เป็นปัจจุบัน
      if (requestResponse && requestResponse.items) {
        setUserRequests(requestResponse.items);
      }
      if (historyResponse && historyResponse.signals) {
        const sortedSignals = [...historyResponse.signals].sort((a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        setAllHistoryData(sortedSignals);
        setAllHistoryDataForArticle(sortedSignals);
      }

      // B. ค้นหาความสดใหม่ของสัญลักษณ์หุ้นตัวนี้จากบอร์ดแอดมิน
      const allSignals = historyResponse?.signals || [];
      const targetSignal = allSignals.find((s: any) => s.ticker === targetSymbol || s.symbol === targetSymbol);

      // 🔴 เคสพิเศษ: ถ้าหุ้นตัวนี้ไม่เคยมีแอดมินให้ข้อมูลมาก่อนเลยในประวัติศาสตร์ -> อนุญาตให้ผ่านฉลุย
      if (!targetSignal) {
        return { success: true };
      }

      // C. ดึงและแปลงฟอร์แมตเส้นของแอดมินเพื่อนำมาจำลอง (Simulation)
      let adminRawLines = [];
      if (targetSignal.details?.lines) {
        adminRawLines = targetSignal.details.lines;
      } else if (targetSignal.details?.S_line || targetSignal.details?.R_line) {
        const sLines = Object.entries(targetSignal.details.S_line || {}).map(([tag, price]) => ({
          tag, price: Number(price), type: 'support'
        }));
        const rLines = Object.entries(targetSignal.details.R_line || {}).map(([tag, price]) => ({
          tag, price: Number(price), type: 'resistance'
        }));
        adminRawLines = [...sLines, ...rLines];
      }

      const currentPrice = currentPrices[targetSymbol];
      if (currentPrice === undefined || currentPrice === null) {
        return { success: false, reason: 'ERROR' };
      }

      // D. 🔥 ทำการตรวจสอบ: ถ้านำเข้าเส้นล่าสุดของแอดมินมาแล้ว เส้นมันจะเหลือ <= 2 เส้นจริงไหม?
      const isEligibleForRequest = verifySRQuotaCount(adminRawLines, currentPrice);

      if (!isEligibleForRequest) {
        // ❌ ถ้านำเข้าแล้วพบว่าเส้นยังเหลือเพียบ (แอดมินอัปเดตไว้แล้วแต่ผู้ใช้ยังไม่อิมพอร์ต)
        // บังคับทำกระบวนการ "Auto-Import" ลงเครื่องผู้ใช้ทันที เพื่อซิงค์หน้าจอให้ตรงกับความจริง
        const dataToSave = {
          lines: adminRawLines,
          supportTags: adminRawLines.filter((l: any) => l.type === 'support').map((l: any) => l.tag),
          resistanceTags: adminRawLines.filter((l: any) => l.type === 'resistance').map((l: any) => l.tag),
        };
        localStorage.setItem(`chartLines_${targetSymbol}`, JSON.stringify(dataToSave));

        // ส่งสัญญาณกลับไปบอกลูกว่าห้ามยิง Request เพราะของใหม่มีให้ใช้เยอะแยะ
        return { success: false, reason: 'STALE' };
      }

      // 🟢 ถ้าตรวจสอบแล้วว่าอัปเดตข้อมูลล่าสุดแล้วเส้นก็ยังเหลือน้อยจริงๆ -> คืนค่าให้ไปยิง Request ได้
      return { success: true };

    } catch (error) {
      console.error("Verification and Import flow failed:", error);
      return { success: false, reason: 'ERROR' };
    }
  };

  const canRequestAnalysis = useMemo(() => {
    // 1. ถ้ายังไม่มีหุ้นที่เลือก หรือ ระบบกำลังโหลดข้อมูลประวัติ (isSignalLoading) 
    // ให้คืนค่า false (ซ่อนปุ่ม) 
    if (!selectedSymbol || isPriceLoading || isSignalLoading) return false;
    // console.log("Checking canRequestAnalysis with:", { selectedSymbol, isSignalLoading, isPriceLoading });

    // 2. เช็คราคาปัจจุบัน ถ้า Undefined (ยังโหลดไม่มา) ให้ซ่อนปุ่มไว้ก่อน
    const currentPrice = currentPrices[selectedSymbol];
    // console.log("Checking canRequestAnalysis with before:", { selectedSymbol, currentPrice });
    if (currentPrice === undefined || currentPrice === null) return false;
    // console.log("Checking canRequestAnalysis with:", { currentPrice });

    // 3. ตรวจสอบประวัติของหุ้นตัวนี้
    const tickerHistory = allHistoryData.filter(h => h.ticker === selectedSymbol);

    // 🔴 ถ้าโหลดเสร็จแล้ว (isSignalLoading เป็น false) แต่ไม่เจอประวัติเลย 
    // แสดงว่าเป็นหุ้นใหม่ที่แอดมินยังไม่เคยทำ -> ให้โชว์ปุ่ม (true)
    // console.log("Checking S/R for:", tickerHistory);
    if (tickerHistory.length === 0) return true;

    const latestUpdate = tickerHistory[0];
    const adminSignals = latestUpdate.signal?.signals || latestUpdate.details?.lines || [];

    // 4. คำนวณ S/R แบบ Dynamic ตามราคาตลาดปัจจุบัน
    let dynamicSupportCount = 0;
    let dynamicResistanceCount = 0;

    adminSignals.forEach((s: any) => {
      if (Number(s.price) <= currentPrice) {
        dynamicSupportCount++;
      } else {
        dynamicResistanceCount++;
      }
    });
    // console.log("Showing Request Button due to low S/R count:", { dynamicSupportCount, dynamicResistanceCount });

    // 🟡 เงื่อนไขที่ 2: ถ้าเส้นฝั่งใดฝั่งหนึ่งเหลือแค่ 1 หรือไม่มีเลย -> โชว์ปุ่มทันที
    if (dynamicSupportCount <= 2 || dynamicResistanceCount <= 2) {
      // console.log("Showing Request Button due to low S/R count:", { dynamicSupportCount, dynamicResistanceCount });
      return true;
    }

    // 🟢 เงื่อนไขที่ 1: เช็คระยะเวลา 30 วัน
    const lastUpdateDate = new Date(latestUpdate.created_at);
    const diffDays = Math.ceil(Math.abs(Date.now() - lastUpdateDate.getTime()) / (1000 * 60 * 60 * 24));

    return diffDays > 30;

  }, [selectedSymbol, allHistoryData, currentPrices, isSignalLoading]);
const activePortfolios = manageData?.allMatches?.filter(m => m.quantity > 0 || m.avgBuyPrice > 0) || [];
const hasActiveData = activePortfolios.length > 0;
  return (
    <div className="p-0 md:p-2 md:h-[calc(100vh-75px)] md:overflow-hidden flex flex-col">
      <style>{`.no-scrollbar::-webkit-scrollbar { display: none; } .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }`}</style>

      {/* --- ส่วนที่แสดงเฉพาะ Mobile เท่านั้น (บนสุด) --- */}
      <div className="w-full mb-4 md:hidden">
        {/* ส่วน Container: ใช้ scrollBox เพื่อเปิดการปัดซ้าย-ขวา */}
        <div
          className={`${scrollBox} px-4 scroll-px-4`}
          onScroll={(e) => handleScroll(e, 0)} // สมมติให้เป็น Dot ชุดที่ 0 (ชุดแรกสุดของหน้า)
        >

          {/* หน้าที่ 1: Portfolio Summary */}
          <div className={scrollItem}>
            <div className="rounded-xl shadow-lg bg-[var(--component-bg-color)] overflow-hidden h-full">
              <PortfolioSummaryList />
            </div>
          </div>

          {/* หน้าที่ 2: Signal Updates */}
          <div className={scrollItem}>
            <div className="h-full">
              <SignalUpdateList
                updates={signalData}
                signalData={signalData}
                allHistoryData={allHistoryData}
                loading={isSignalLoading}
                error={signalError}
                onRefresh={handleFetchSignals}
                onSelectSymbol={(newSymbol) => setSelectedSymbol(newSymbol)}
                onImportClick={(ticker, isBulk) => handleUserImportFromSignal(ticker, isBulk)}
                portfolios={portfolios} // ส่งข้อมูลพอร์ตไปด้วยเพื่อใช้แสดงใน SignalUpdateList
              />
            </div>
          </div>

        </div>

        {/* จุดไข่ปลา (Dots) บอกตำแหน่งการปัดสำหรับชุดนี้ */}
        <div className="mt-2">
          <Dot count={2} activeIdx={activeDots[0]} />
        </div>
      </div>

      {/* ✅ Grid Container เพิ่ม flex-1 และ min-h-0 ให้เต็มพื้นที่หน้าจอส่วนที่เหลือ */}
      <div ref={gridContainerRef} className="flex flex-col md:flex-row gap-6 md:gap-2 flex-1 md:min-h-0">

        {/* --- คอลัมน์ที่ 1: ฝั่งซ้าย (Desktop) | ส่วนที่ 2.2 & 2.3 (Mobile) --- */}
        <div
          className={`flex flex-col gap-2 md:h-full md:min-h-0 min-w-0 ${isMobile ? 'order-2' : 'order-1'}`}
          style={!isMobile ? { order: 1, flexBasis: `${colWidths.left}%`, maxWidth: `${colWidths.left}%` } : undefined}
        >

          <div
            ref={leftColScrollRef}
            className={`
              flex flex-1 md:flex-col gap-4 w-full  
              md:overflow-y-auto md:pr-1 md:pb-10 no-scrollbar 
              overflow-x-auto snap-x snap-mandatory 
              px-4 md:px-0 
              scroll-px-4 md:scroll-p-0
            `}
            onScroll={(e) => handleScroll(e, 0)}
          >

            {/* 3. พอร์ตหุ้นทั้งหมด (PortfolioSummaryList) - โชว์แค่ Desktop */}
            {!isMobile && (
              <div className="w-full order-1">
                <PortfolioSummaryList />
              </div>
            )}
            {!isMobile && (
              <div className="w-full mb-2 order-1">
                <RequestBox
                  isAdmin={userInfo?.isAdmin}
                  // 💡 ปรับโค้ดตรงนี้: ดักเช็คทั้ง s.ticker และ s.symbol พร้อมตรวจสอบให้มั่นใจว่าเป็น string ชัวร์ๆ
                  portfolios={portfolios}
                />
              </div>
            )}
            {!isMobile && (
              <div className="w-full order-1">
                <SignalUpdateList
                  updates={signalData}
                  loading={isSignalLoading}
                  signalData={signalData}
                  allHistoryData={allHistoryData}
                  error={signalError}
                  onRefresh={handleFetchSignals} // ส่งฟังก์ชันไปให้ปุ่มในลูกกดเรียก
                  onSelectSymbol={(newSymbol) => setSelectedSymbol(newSymbol)}
                  onImportClick={(ticker, isBulk) => handleUserImportFromSignal(ticker, isBulk)}
                  portfolios={portfolios} // ส่งข้อมูลพอร์ตไปด้วยเพื่อใช้แสดงใน SignalUpdateList
                />
              </div>

            )}

            {/* 2. รายการโปรด (Desktop เท่านั้นที่ตำแหน่งนี้) */}
            {!isMobile && (
              <div className="w-full mb-2 order-2">
                <Favorite onSelect={setSelectedSymbol} />
              </div>
            )}


          </div> {/* ปิด Scroll Box */}

        </div>

        {/* --- Drag Handle: ฝั่งซ้าย <-> กลาง (Desktop เท่านั้น) --- */}
        {!isMobile && (
          <div
            onPointerDown={handleColDragStart(0)}
            onDoubleClick={() => setColWidths(DEFAULT_COL_WIDTHS)}
            className="hidden md:flex relative z-10 items-center justify-center w-1 shrink-0 cursor-col-resize group touch-none select-none"
            style={{ order: 2 }}
            title="ลากเพื่อปรับความกว้าง (ดับเบิลคลิกเพื่อรีเซ็ต)"
          >
            {/* พื้นที่สัมผัสกว้างขึ้นสำหรับนิ้วบน iPad โดยไม่กินความกว้างของ layout */}
            <div className="absolute inset-y-0 -left-3 -right-3" />
            <div className={`w-1 h-60 rounded-full transition-colors ${isDraggingCol && dragInfoRef.current?.boundary === 0 ? 'bg-yellow-500/60' : 'bg-white/10 group-hover:bg-yellow-500/60'}`} />
          </div>
        )}

        {/* --- คอลัมน์ที่ 2: ฝั่งกลาง (Desktop) | ส่วนที่ 1 (Mobile) --- */}
        <div
          className={`flex flex-col gap-6 md:h-full md:min-h-0 min-w-0 ${isMobile ? 'order-1' : 'order-2'}`}
          style={!isMobile ? { order: 3, flexBasis: `${colWidths.middle}%`, maxWidth: `${colWidths.middle}%` } : undefined}
        >
          {/* ✅ คอลัมน์กลางใช้ flex-1 และ overflow-y-auto */}
          <div className={`
            ${scrollBox}
            px-4 md:px-0 
            scroll-px-4 md:scroll-px-0
            md:flex-1 md:overflow-y-auto md:pr-1 md:pb-10 no-scrollbar
          `} onScroll={(e) => handleScroll(e, 1)}>
            <div className={scrollItem}>
              <div className="rounded-xl shadow-lg p-2 bg-[var(--component-bg-color)]">
                <TradingView
                  symbol={selectedSymbol}
                  priceLineType={priceLineType}
                  setPriceLineType={setPriceLineType}
                  onSupportResistanceUpdate={handleSupportResistanceUpdate}
                  onClearAllSR={() => { setFreestyleSR({}); setAutoSR({}); }}
                  avgBuyPrice={avgBuyPrice}
                  onIndicatorsReady={handleIndicatorsReady}
                  modeLoss={modeLoss}
                  setModeLoss={setModeLoss}
                  onPriceClick={(p: number) => setCustomPrice(p)}
                  isAdmin={userInfo?.isAdmin}
                  signalData={fullSignalList}
                  onUserImport={handleUserImportFromSignal}
                  showRequestButton={canRequestAnalysis}
                  quotaInfo={requestQuota}
                  onImportAndVerifyRequest={handleImportAndVerifyRequest} // ✅ ส่งฟังก์ชันคุมแผนรับมือใหม่ลงไป
                  onRequestSuccess={fetchUserRequests}   // รีเฟรชโควตาเมื่อส่งสำเร็จ
                  isNewSignalAvailable={showNewTagOnImportBtn}

                />
              </div>
            </div>
            {/* บทวิเคราะห์ AI ยังรองรับเฉพาะหุ้น → ซ่อนทั้งกล่อง (และไม่เรียก analyzeAI) เมื่อดูกราฟคริปโต */}
            {!isCryptoSymbol(selectedSymbol) && (
              <div className={scrollItem}>
                <div className="rounded-xl mt-2 shadow-lg p-2 bg-[var(--component-bg-color)] h-full">
                  <ArticleByAI aiPayload={aiPayload} />
                </div>
              </div>
            )}
            {/* 2.3 Favorite (ย้ายมาต่อท้าย ArticleAI แล้ว) */}
            {isMobile && (
              <div className={scrollItem}>
                <div className="rounded-xl shadow-lg p-2 bg-[var(--component-bg-color)]">
                  <Favorite onSelect={setSelectedSymbol}  />
                </div>
              </div>
            )}
          </div>
          <Dot count={3} activeIdx={activeDots[1]} />
        </div>

        {/* --- Drag Handle: ฝั่งกลาง <-> ขวา (Desktop เท่านั้น) --- */}
        {!isMobile && (
          <div
            onPointerDown={handleColDragStart(1)}
            onDoubleClick={() => setColWidths(DEFAULT_COL_WIDTHS)}
            className="hidden md:flex relative z-10 items-center justify-center w-1 shrink-0 cursor-col-resize group touch-none select-none"
            style={{ order: 4 }}
            title="ลากเพื่อปรับความกว้าง (ดับเบิลคลิกเพื่อรีเซ็ต)"
          >
            <div className="absolute inset-y-0 -left-3 -right-3" />
            <div className={`w-1 h-60 my-8 rounded-full transition-colors ${isDraggingCol && dragInfoRef.current?.boundary === 1 ? 'bg-yellow-500/60' : 'bg-white/10 group-hover:bg-yellow-500/60'}`} />

          </div>
        )}

        {/* --- คอลัมน์ที่ 3: ฝั่งขวา (Desktop) | ส่วนที่ 3 (Mobile) --- */}
        <div
          className={`flex flex-col gap-2 order-3 md:h-full md:min-h-0 min-w-0`}
          style={!isMobile ? { order: 5, flexBasis: `${colWidths.right}%`, maxWidth: `${colWidths.right}%` } : undefined}
        >

          {/* ✅ คอลัมน์ขวาใช้ flex-1 และ overflow-y-auto */}
          <div
            className={`
              block w-full 
              md:flex md:flex-col md:flex-1 md:overflow-y-auto 
              md:pr-1 md:pb-10 no-scrollbar
              px-4 md:px-0
            `}
          >
            {/* 1. จัดการพอร์ต (โชว์ทั้งคู่ แต่ Mobile จะเรียงต่อกัน) */}
            <div className="w-full mb-4 md:mb-2">
              <div className="rounded-xl shadow-lg bg-[var(--component-bg-color)] overflow-hidden">

                <button
                  className="w-full flex flex-row items-start p-2 text-[var(--text-color)] transition-colors hover:bg-black/5 gap-6"
                  onClick={() => setShowManagePortfolio(!showManagePortfolio)}
                >
                  {/* ส่วนที่ 1: ชื่อพอร์ตและ Symbol (ชิดซ้าย) */}
                  <div className="flex flex-col items-start min-w-fit">
                    <div className="flex items-center gap-1 mb-1.5">
                      <span className="text-[10px] font-bold uppercase  tracking-wider">จัดการพอร์ต</span>
                      <span className="text-[11px] font-black text-[var(--yellow-text-color)]">
                        [{cryptoPairLabel(selectedSymbol)}]
                      </span>
                    </div>

                    {!showManagePortfolio && manageData?.selected && hasActiveData && (
                      <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                        {activePortfolios.length > 1 ? (
                          <select
                            className="bg-white/5 hover:bg-white/10 text-[var(--text-color)] px-2 py-0.5 rounded border border-white/10 outline-none text-[11px] font-bold transition-colors"
                            style={{ backgroundColor: 'var(--bg-item)' }}
                            value={manageData.selected.subPortId}
                            onChange={(e) => {
                              const selected = manageData.allMatches.find(m => m.subPortId === e.target.value);
                              if (selected) handleSelectPortfolio(selected.portId, selected.subPortId);
                            }}
                          >
                            {manageData.allMatches.map((m: any) => (
                              <option key={m.subPortId} value={m.subPortId}>{m.subPortName}</option>
                            ))}
                          </select>
                        ) : (
                          <span className="text-xs font-bold  truncate max-w-[120px] leading-none py-1">
                            {manageData.selected.subPortName}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* ส่วนที่ 2: ข้อมูลตัวเลข (ดันไปทางขวา) */}
                  {!showManagePortfolio && manageData?.selected && (
                    (manageData.selected.quantity > 0 || manageData.selected.avgBuyPrice > 0) && (
    <div className="flex flex-row items-start ml-auto gap-5">
                      {/* จำนวนหุ้น */}
                      <div className="flex flex-col items-start">
                        <span className="text-[10px] uppercase font-bold mb-1.5">{manageData.selected.isCrypto ? 'จำนวนเหรียญ' : 'จำนวนหุ้น'}</span>
                        <span className="text-[14px] font-bold text-[var(--green-text-color)] leading-none">
                          {manageData.selected.isCrypto
                            ? shortenDecimalText(formatCryptoQty(manageData.selected.quantityRaw), 8)
                            : manageData.selected.quantity.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </span>
                      </div>

                      {/* ราคาเฉลี่ย */}
                      <div className="flex flex-col items-start border-l border-white/10 pl-4">
                        <span className="text-[10px] uppercase font-bold  mb-1.5">ราคาเฉลี่ย</span>
                        <span className="text-[15px] font-bold text-[var(--yellow-text-color)] leading-none">
                          ${manageData.selected.avgBuyPrice.toFixed(2)}
                        </span>
                      </div>

                      {/* กำไร/ขาดทุน */}
                      <div className="flex flex-col items-start border-l border-white/10 pl-4">
                        <span className="text-[10px] uppercase font-bold  mb-1.5">กำไร/ขาดทุน</span>
                        {(() => {
                          const s = manageData.selected;
                          const p = manageData.currentPrice;
                          const totalCost = s.quantity * s.avgBuyPrice;
                          const currentValue = s.quantity * p;
                          const plAmount = p > 0 ? currentValue - totalCost : 0;
                          const plPct = (p > 0 && s.avgBuyPrice > 0) ? ((p - s.avgBuyPrice) / s.avgBuyPrice) * 100 : 0;
                          const isProfit = plAmount >= 0;
                          const colorClass = isProfit ? 'text-[var(--green-text-color)]' : 'text-[var(--red-text-color)]';

                          return (
                            <div className="flex flex-col items-start gap-1 leading-none">
                              <span className={`text-[14px] font-black ${colorClass}`}>
                                {isProfit ? '+' : '-'}${s.isCrypto ? formatCryptoUsd(Math.abs(plAmount)) : Math.abs(plAmount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                              <span className={`text-[10px] font-bold ${colorClass}`}>
                                ({isProfit ? '▲' : '▼'} {plPct.toFixed(2)}%)
                              </span>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                    )
                  )}

                  {/* ส่วนที่ 3: ปุ่มลูกศร */}
                  <div className={`transition-transform duration-200 ml-auto flex items-start pt-1 ${showManagePortfolio ? 'rotate-0' : ''}`}>
                    <span className="text-base hover:opacity-100 transition-opacity">
                      {showManagePortfolio ? "▲" : "▼"}
                    </span>
                  </div>
                </button>
                {/* เรียกใช้ Modal */}
                <ImportConflictModal
                  isOpen={importModal.isOpen}
                  symbol={importModal.symbol}
                  onClose={() => setImportModal({ ...importModal, isOpen: false })}
                  onConfirm={(mode) => executeImport(importModal.symbol, importModal.signal, mode, false)}
                />
                {showManagePortfolio && (
                  <div className="p-2 border-t border-white/5">
                    <ManagePortfolioV2
                      symbol={selectedSymbol}
                      portfolios={portfolios}
                      onRefresh={refreshUserPortfolios}
                      onSelectPortfolio={handleSelectPortfolio}
                      setUpdateTrigger={setUpdateTrigger}
                      currentPrices={currentPrices}
                    // onSummaryUpdate={setManageData}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* 2. คำนวณค่าเฉลี่ยล่วงหน้า */}
            <div className="w-full mb-4 md:mb-2">
              <div className="rounded-xl shadow-lg bg-[var(--component-bg-color)]">
                <button className="w-full flex justify-between p-4 font-bold text-[var(--text-color)]" onClick={() => setShowAverageCalculate(!showAverageCalculate)}>
                  <span>คำนวณค่าเฉลี่ยล่วงหน้า</span> {showAverageCalculate ? "▲" : "▼"}
                </button>
                {showAverageCalculate && (
                  <div className="p-2">
                    <AverageCalculate
                      symbol={selectedSymbol}
                    portfolioId={manageData?.selected?.portId || defaultPortfolioInfo.portId}
  subPortId={manageData?.selected?.subPortId || defaultPortfolioInfo.subPortId}
                      avgBuyPrice={avgBuyPrice}
                      updateTrigger={updateTrigger}
                      currentPrices={currentPrices}
                      // displayMode={displayMode}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* 3. ตารางคำนวณ แนวรับ-แนวต้าน */}
            <div className="w-full mb-4 md:mb-2">
              <div className="rounded-xl shadow-lg bg-[var(--component-bg-color)]">
                <button className="w-full flex justify-between p-4 font-bold text-[var(--text-color)]" onClick={() => setShowTableCalculate(!showTableCalculate)}>
                  <span>ตารางคำนวณ แนวรับ-แนวต้าน</span> {showTableCalculate ? "▲" : "▼"}
                </button>
                {showTableCalculate && (
                  <div className="p-2">
                    <TableCalculate
                      supportResistanceData={
                        priceLineType === 'freestyle'
                          ? freestyleSR[selectedSymbol] || []
                          : autoSR[selectedSymbol] || []
                      }
                      symbol={selectedSymbol}
                    // currentPrice={currentPrices[selectedSymbol] || null}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* 4. คำนวณความเสียหายล่วงหน้า */}
            <div className="w-full mb-4 md:mb-2">
              <div className="rounded-xl shadow-lg bg-[var(--component-bg-color)]">
                <button className="w-full flex justify-between p-4 font-bold text-[var(--text-color)]" onClick={() => setShowLossCalculator(!showLossCalculator)}>
                  <span>คำนวณความเสียหายล่วงหน้า</span> {showLossCalculator ? "▲" : "▼"}
                </button>
                {showLossCalculator && (
                  <div className="px-2 pb-2">
                  <LossCalculator
                      symbol={selectedSymbol}
                      setModeLoss={setModeLoss}
                      customPrice={customPrice}
                      setCustomPrice={setCustomPrice}
                      // อุดรอยรั่วด้วยการดึง ID จาก manageData มาใช้ถ้า State หลักหลุด
                      portfolioId={manageData?.selected?.portId || defaultPortfolioInfo.portId || selectedPortfolioId}
                      subPortId={manageData?.selected?.subPortId || defaultPortfolioInfo.subPortId || selectedSubPortId}
                      avgBuyPrice={manageData?.selected?.avgBuyPrice || avgBuyPrice}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ซ่อน Dot บน Mobile ไปเลยเพราะไม่ได้ปัดแล้ว */}
          <div className="md:hidden h-10" />
        </div>

      </div>
    </div>
  );
};

export default HomePage;